import { ChangeDetectionStrategy, Component, effect, inject, input, viewChild } from '@angular/core';
import { FormDefinitionTabStateStore } from './form.definition.tab.state';
import { WbSurfaceComponent } from './wb.surface.component';
import type { AnyWbSurfaceDefinition } from './wb.surface.definition';

/**
 * Mounts a formDefinition tab's child Form and reports dirty/saving/error chrome
 * to the host store. Keep this component mounted while another outer tab is
 * selected so pending blur saves are not destroyed.
 */
@Component({
  selector: 'wb-form-definition-tab-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WbSurfaceComponent],
  template: '@if (definition(); as def) { <wb-surface [definition]="def" /> }',
  styles: [`
    :host {
      display: flex;
      flex: 1 1 auto;
      min-height: 0;
      min-width: 0;
      width: 100%;
    }
    wb-surface {
      display: flex;
      flex: 1 1 auto;
      min-height: 0;
      min-width: 0;
      width: 100%;
      height: auto;
    }
  `],
})
export class WbFormDefinitionTabHostComponent {

  private readonly stateStore = inject(FormDefinitionTabStateStore, { optional: true });
  private readonly surface = viewChild(WbSurfaceComponent);

  public readonly tabPath = input.required<string>();
  public readonly definition = input<AnyWbSurfaceDefinition | null>(null);
  /** Kept for callers; visibility is owned by the Manage host wrapper. */
  public readonly active = input(true);

  public constructor() {

    effect(() => {
      const store = this.stateStore;
      const tabPath = this.tabPath();
      const page = this.surface()?.$page() ?? null;
      if (!store) return;
      if (!page) {
        store.clear(tabPath);
        return;
      }
      store.set(tabPath, {
        dirty: page.$dirty(),
        saving: page.$saving(),
        errorCount: page.$surfaceErrors().length,
      });
    });
  }
}
